import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Badge,
  Button,
} from 'reactstrap';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageGuard from '../../components/Common/PageGuard';
import PermissionGate from '../../components/Common/PermissionGate';
import { GET_USERS, GetUsersData, GetUsersVariables } from '../../graphql/queries/users';

const UsersSuspend: React.FC = () => {
  document.title = 'Suspended Users | NGK Tours';

  const [pageSize] = useState(10);

  // Fetch users from GraphQL
  const { data, loading, error, refetch } = useQuery<GetUsersData, GetUsersVariables>(
    GET_USERS,
    {
      variables: {
        first: pageSize,
        where: {},
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }
  );

  // Prepare suspended users data for table
  const users = useMemo(() => {
    if (!data?.users?.nodes) return [];

    return data.users.nodes
      .map((user) => ({
        id: user.databaseId,
        userId: user.id,
        name: user.name || user.username || 'N/A',
        email: user.email,
        username: user.username,
        roles: user.roles.nodes.map((role) => role.name),
        registeredDate: user.registeredDate
          ? new Date(user.registeredDate).toLocaleDateString()
          : 'N/A',
      }))
      .filter((user) => user.roles.includes('suspended_user'));
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id',
        width: '5%',
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (cellProps: any) => {
          const user = cellProps.row.original;
          return (
            <div>
              <h5 className="font-size-14 mb-1">
                <Link to={`/users/${user.id}`} className="text-dark">
                  {user.name}
                </Link>
              </h5>
              <p className="text-muted mb-0">{user.email}</p>
            </div>
          );
        },
      },
      {
        Header: 'Username',
        accessor: 'username',
      },
      {
        Header: 'Status',
        accessor: 'roles',
        Cell: (cellProps: any) => {
          const roles = cellProps.row.original.roles || [];
          const isSuspended = roles.includes('suspended_user');

          return (
            <div className="d-flex flex-wrap gap-1">
              {isSuspended && (
                <Badge color="secondary" className="mb-1">
                  Suspended
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Registered',
        accessor: 'registeredDate',
      },
    ],
    []
  );

  return (
    <PageGuard requiredCapabilities={['business_user_suspend']}>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Users" breadcrumbItem="Suspended Users" />

          <Row className="align-items-center mb-3">
            <Col md={6}>
              <div>
                <h5 className="card-title">
                  Suspended Users{' '}
                  <span className="text-muted fw-normal ms-2">
                    ({users.length})
                  </span>
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                <Button
                  color="light"
                  onClick={() => refetch()}
                  disabled={loading}
                >
                  <i className="bx bx-refresh me-1"></i> Refresh
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading && (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading suspended users...</p>
                    </div>
                  )}

                  {error && (
                    <Alert color="danger">
                      <h5>Error loading suspended users</h5>
                      <p>{error.message}</p>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => refetch()}
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {!loading && !error && (
                    <>
                      {users.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted">No suspended users found</p>
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={users}
                          isGlobalFilter={true}
                          customPageSize={pageSize}
                          className="table align-middle datatable dt-responsive table-check nowrap"
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </PageGuard>
  );
};

export default UsersSuspend;

